import type { User } from "features/auth/authTypes.ts";
import type {
  PermissionsParsed,
  RolePermissions,
} from "features/permissions/permissionsTypes.ts";
import { initialStatePermissions } from "features/permissions/permissionsTypes.ts";

export const IMPERSONATION_STATE_STORAGE_KEY = "permissionInspectorImpersonation";
export const IMPERSONATION_ORIGINAL_USER_STORAGE_KEY =
  "permissionInspectorOriginalUser";
export const IMPERSONATION_ORIGINAL_PERMISSIONS_STORAGE_KEY =
  "permissionInspectorOriginalPermissions";
export const IMPERSONATION_TOGGLE_EVENT = "permission-inspector-impersonation";

export type PermissionInspectorImpersonationState = {
  impersonatedUserId: number;
  impersonatedUserName: string;
  impersonatedUserRole: string;
};

const isBrowser = () => typeof window !== "undefined";

export const parsePermissionsFromRolePermissions = (
  rolePermissions: RolePermissions[],
): PermissionsParsed => {
  const parsedPermissions = { ...initialStatePermissions };
  const parsedPermissionKeys = Object.keys(parsedPermissions) as Array<
    keyof PermissionsParsed
  >;

  parsedPermissionKeys.forEach((permissionKey) => {
    const matchingPermission = rolePermissions.find(
      (permission) => permission.ModuleName === permissionKey,
    );

    if (!matchingPermission) {
      return;
    }

    parsedPermissions[permissionKey] = {
      id: matchingPermission.Id,
      isCreate: matchingPermission.IsCreate,
      isDelete: matchingPermission.IsDelete,
      isRead: matchingPermission.IsRead,
      isUpdate: matchingPermission.IsUpdate,
      moduleId: matchingPermission.ModuleId,
      moduleName: permissionKey,
      roleId: matchingPermission.RoleId,
    };
  });

  return parsedPermissions;
};

export const getImpersonationState = () => {
  if (!isBrowser()) {
    return null as PermissionInspectorImpersonationState | null;
  }

  const state = sessionStorage.getItem(IMPERSONATION_STATE_STORAGE_KEY);

  if (!state) {
    return null;
  }

  try {
    return JSON.parse(state) as PermissionInspectorImpersonationState;
  } catch {
    return null;
  }
};

export const dispatchImpersonationChangedEvent = () => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new CustomEvent(IMPERSONATION_TOGGLE_EVENT));
};

export const clearImpersonationStorage = () => {
  if (!isBrowser()) {
    return;
  }

  sessionStorage.removeItem(IMPERSONATION_STATE_STORAGE_KEY);
  sessionStorage.removeItem(IMPERSONATION_ORIGINAL_USER_STORAGE_KEY);
  sessionStorage.removeItem(IMPERSONATION_ORIGINAL_PERMISSIONS_STORAGE_KEY);
  dispatchImpersonationChangedEvent();
};

export const storeOriginalImpersonationContext = (
  user: User,
  permissions: PermissionsParsed,
) => {
  if (!isBrowser()) {
    return;
  }

  sessionStorage.setItem(IMPERSONATION_ORIGINAL_USER_STORAGE_KEY, JSON.stringify(user));
  sessionStorage.setItem(
    IMPERSONATION_ORIGINAL_PERMISSIONS_STORAGE_KEY,
    JSON.stringify(permissions),
  );
};

export const getOriginalImpersonationContext = () => {
  if (!isBrowser()) {
    return null as { user: User; permissions: PermissionsParsed } | null;
  }

  const user = sessionStorage.getItem(IMPERSONATION_ORIGINAL_USER_STORAGE_KEY);
  const permissions = sessionStorage.getItem(
    IMPERSONATION_ORIGINAL_PERMISSIONS_STORAGE_KEY,
  );

  if (!user || !permissions) {
    return null;
  }

  try {
    return {
      user: JSON.parse(user) as User,
      permissions: JSON.parse(permissions) as PermissionsParsed,
    };
  } catch {
    return null;
  }
};

export const setImpersonationState = (
  state: PermissionInspectorImpersonationState,
) => {
  if (!isBrowser()) {
    return;
  }

  sessionStorage.setItem(IMPERSONATION_STATE_STORAGE_KEY, JSON.stringify(state));
  dispatchImpersonationChangedEvent();
};
